import { useCallback, useMemo } from 'react';
import { Button } from 'react-bootstrap';
import { Copy, Robot } from 'react-bootstrap-icons';
import classNames from 'classnames';
import {
	AnswerBySource,
	FollowUpQuestionSuggestion,
	individualResponseSourceOptions,
	TempCitation,
} from '@/lib/models';
import { createUseThemedStyles, useCopyTextToClipboard } from '@/hooks';
import { AiAnswerReferences, AiAnswerSources, Typewriter } from '@/components';
import { isAbsoluteUrl } from '@/lib/utils';
import { renderToStaticMarkup } from 'react-dom/server';

const useStyles = createUseThemedStyles((theme) => ({
	aiIconOuter: {
		width: 40,
		flexShrink: 0,
		marginRight: 20,
	},
	aiIcon: {
		width: '100%',
		borderRadius: '50%',
		position: 'relative',
		paddingBottom: '100%',
		backgroundColor: theme.colors.primary,
		'& > svg': {
			top: '50%',
			left: '50%',
			position: 'absolute',
			transform: 'translate(-50%, -50%)',
		},
	},
	typewriterOuter: {
		flex: 1,
	},
}));

export enum CHAT_CONTENT_TYPE_ID {
	QUESTION = 'QUESTION',
	ANSWER = 'ANSWER',
	PAST_ANSWER = 'PAST_ANSWER',
}

export interface ChatContent {
	conversationQueryId: string;
	chatContentId: string;
	chatContentTypeId: CHAT_CONTENT_TYPE_ID;
	content: string;
	references: string[];
	sources: TempCitation[];
	followUpQuestions?: FollowUpQuestionSuggestion[];
	rated: boolean;
	showMore?: boolean;
	typeOfData?: string;
	answerBySources?: AnswerBySource[];
}

interface AiAnswerProps {
	context: ChatContent;
	onShowMoreButtonClick(context: ChatContent): void;
	onRateButtonClick(context: ChatContent): void;
	onFollowUpButtonClick(suggestion: FollowUpQuestionSuggestion, context: ChatContent): void;
	onAnimationComplete(): void;
	isPast?: boolean;
	aiConversationId?: string;
}

export const AiAnswer = ({
	context,
	onShowMoreButtonClick,
	onRateButtonClick,
	onFollowUpButtonClick,
	onAnimationComplete,
	isPast,
	aiConversationId,
}: AiAnswerProps) => {
	const classes = useStyles();
	const { copyHtmlToClipboard } = useCopyTextToClipboard();

	const addChatRefToLinks = useCallback(
		(html: string): string => {
			const parser = new DOMParser();
			const doc = parser.parseFromString(html, 'text/html');
			const anchors = doc.querySelectorAll('a');

			anchors.forEach((anchor) => {
				const href = anchor.getAttribute('href');

				if (!href) {
					return;
				}

				const isExternal = isAbsoluteUrl(href);
				const url = isExternal ? new URL(href) : new URL(href, window.location.origin);

				if (!isExternal) {
					url.searchParams.set('ref', 'chat');

					if (aiConversationId) {
						url.searchParams.set('aiConversationIdNav', aiConversationId);
					}
				}

				anchor.setAttribute('href', url.toString());
				anchor.setAttribute('target', '_blank');
				anchor.setAttribute('rel', 'noopener noreferrer');
			});

			return doc.body.innerHTML;
		},
		[aiConversationId]
	);

	const htmlString = useMemo(() => {
		if (!context.answerBySources) {
			return addChatRefToLinks(context.content);
		}

		if (context.answerBySources.length <= 0) {
			return addChatRefToLinks(context.content);
		}

		return addChatRefToLinks(
			context.answerBySources
				.map((i, loopIndex) => {
					const source = individualResponseSourceOptions.find((j) => j.aiResponseSourceId === i.source);
					const IconComponent = source?.icon;
					const iconMarkup = IconComponent
						? renderToStaticMarkup(<IconComponent className="text-primary mr-2" width={24} height={24} />)
						: '';
					const isLastSource = (context.answerBySources ?? []).length - 1 === loopIndex;

					return `
						<div class="mb-8">
							<h3 class="mb-4 d-flex align-items-center">
							${iconMarkup}
							<span>From ${source?.title ?? i.source}</span>
							</h3>
							${i.content}
						</div>
						${!isLastSource ? '<hr class="mb-8"/>' : ''}
					`;
				})
				.join('')
		);
	}, [addChatRefToLinks, context.answerBySources, context.content]);

	const handleCopyButtonClick = () => {
		copyHtmlToClipboard(htmlString);
	};

	return (
		<div className="d-flex">
			<div className={classes.aiIconOuter}>
				<div className={classes.aiIcon}>
					<Robot className="text-white" width={20} height={20} />
				</div>
			</div>
			<div className={classes.typewriterOuter}>
				<Typewriter
					delayBetweenCharacters={isPast ? 0 : 10}
					htmlString={htmlString}
					onAnimationComplete={onAnimationComplete}
				>
					{context.references.length > 0 && (
						<AiAnswerReferences className="mb-4" references={context.references} />
					)}
					{context.sources.length > 0 && <AiAnswerSources className="mb-4" sources={context.sources} />}
					<div
						className={classNames('d-flex justify-content-between', {
							'mb-2': (context.followUpQuestions ?? []).length > 0,
						})}
					>
						<div className="d-flex align-items-center">
							{context.showMore && (
								<Button
									size="sm"
									variant="outline-primary"
									onClick={() => onShowMoreButtonClick(context)}
								>
									Show more
								</Button>
							)}
						</div>
						<div className="d-flex align-items-center">
							<Button
								size="sm"
								variant="outline-primary"
								className="d-flex align-items-center mr-2"
								style={{ padding: 12 }}
								onClick={handleCopyButtonClick}
							>
								<Copy />
							</Button>
							<Button
								size="sm"
								variant={context.rated ? 'primary' : 'outline-primary'}
								onClick={() => onRateButtonClick(context)}
								disabled={context.rated}
							>
								{context.rated ? 'Rating Recieved' : 'Rate Answer'}
							</Button>
						</div>
					</div>
					{(context.followUpQuestions ?? []).length > 0 && (
						<>
							<p className="mb-4">
								<strong>Recommended follow-up questions:</strong>
							</p>
							{(context.followUpQuestions ?? []).map((q) => (
								<div
									key={q.suggestionId}
									className="d-flex align-items-center mb-2 py-2 px-3 bg-gray-100 border rounded"
									onClick={() => onFollowUpButtonClick(q, context)}
								>
									<p className="mb-0 small">{q.suggestion}</p>
								</div>
							))}
						</>
					)}
				</Typewriter>
			</div>
		</div>
	);
};
